import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Tooltip,
  VStack,
  useClipboard,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useClient } from 'urql';
import { Select as ChakraReactSelect } from 'chakra-react-select';
import { useTranslation } from 'react-i18next';
import { GetExternalAccessTokenDocument } from '../../gql/graphql';
import { useSavedViews } from '../../hooks/saved-views';
import { useUserTenant } from '../../services/auth-info';
import { EventName, useMixpanel } from '../../services/mixpanel';
import { parseExternalAccessToken } from '../../utils/jwt-utils';
import { toLocaleDateStringWithHoursAndMinutes } from '../../utils/time-utils';
import { useBreadcrumb } from '../common/BreadcrumbProvider';

type OrderSharingModalProps = {
  defaultOrder: { id: string; name: string };
  orders: { id: string; name: string }[];
  isOpen: boolean;
  onClose: () => void;
};

type ExpiresInChoice = {
  label: string;
  value: number;
};

type ProjectPathParams = {
  projectId: string;
};

// eslint-disable-next-line complexity
export default function OrderSharingModal({
  defaultOrder,
  orders,
  isOpen,
  onClose,
}: OrderSharingModalProps) {
  const client = useClient();
  const [error, setError] = useState<string | null>();

  const { t } = useTranslation('project');

  // Generate a list of choices for the expiry time
  const EXPIRY_CHOICES: ExpiresInChoice[] = useMemo(
    () => [
      {
        label: t('table-sharing-modal.expiration.5-minutes'),
        value: 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.24-hours'),
        value: 24 * 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.7-days'),
        value: 7 * 24 * 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.14-days'),
        value: 14 * 24 * 60 * 60,
      },
      {
        label: t('table-sharing-modal.expiration.28-days'),
        value: 28 * 24 * 60 * 60,
      },
    ],
    [t]
  );

  const [expiresIn, setExpiresIn] = useState<number>(EXPIRY_CHOICES[2].value);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [selectedOrders, setSelectedOrders] = useState([defaultOrder]);

  const { projectId } = useParams<
    keyof ProjectPathParams
  >() as ProjectPathParams;

  const { tenant } = useUserTenant();
  const { projectName, orderName } = useBreadcrumb();

  const { trackEvent } = useMixpanel();

  const { savedViews } = useSavedViews(projectId, false);

  const [viewId, setViewId] = useState<string | undefined>(undefined);

  let shareableUrl: string;
  if (accessToken) {
    shareableUrl = `${window.location.origin}/login?access_token=${accessToken}`;
    if (viewId) {
      shareableUrl += `&view=${viewId}`;
    }
  } else {
    shareableUrl = '';
  }

  const { hasCopied, onCopy } = useClipboard(shareableUrl, {
    format: 'text/plain',
    // This is the default
    timeout: 1500,
  });

  useEffect(() => {
    if (hasCopied) {
      trackEvent(EventName.TableShared, {
        'Project Name': projectName,
        'Project ID': projectId,
        'Table Name': orderName,
        'Expiration Duration': EXPIRY_CHOICES.find(
          (choice) => choice.value === expiresIn
        )?.label,
      });
    }
  }, [
    EXPIRY_CHOICES,
    expiresIn,
    hasCopied,
    orderName,
    projectId,
    projectName,
    tenant?.name,
    trackEvent,
  ]);

  useEffect(() => {
    if (savedViews && savedViews.length > 0) {
      let homeViewId = savedViews.find((view) => view.isDefault)?.id;
      if (homeViewId) {
        setViewId(homeViewId);
      }
    }
  }, [savedViews]);

  const fetchToken = useCallback(
    async (expiresIn: number) => {
      const result = await client.query(
        GetExternalAccessTokenDocument,
        {
          orderIds:
            selectedOrders.length > 0
              ? selectedOrders.map((order) => order.id)
              : [defaultOrder.id],
          expiresIn,
        },
        { requestPolicy: 'network-only' }
      );
      if (!result.data?.externalAccessToken?.accessToken) {
        setError('Could not get access token');
      } else {
        const accessToken = result.data?.externalAccessToken?.accessToken;
        setAccessToken(accessToken);
      }
    },
    [client, defaultOrder.id, selectedOrders]
  );

  useEffect(() => {
    if (isOpen) {
      setError(null);
      fetchToken(expiresIn);
    } else {
      setAccessToken(null);
      setError(null);
    }
  }, [isOpen, fetchToken, expiresIn]);

  let modalBodyElement: React.ReactElement | null = null;

  if (error) {
    modalBodyElement = (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  } else if (accessToken && shareableUrl.length > 0) {
    invariant(accessToken, 'Access token should be set');
    const tokenPayload = parseExternalAccessToken(accessToken);
    const expiry = toLocaleDateStringWithHoursAndMinutes(
      tokenPayload.exp * 1000
    );

    modalBodyElement = (
      <VStack>
        <Text alignSelf="flex-start">
          {t('table-sharing-modal.select-tables')}
        </Text>
        <Box width="100%">
          <ChakraReactSelect
            isMulti
            closeMenuOnSelect={false}
            value={selectedOrders.map((order) => ({
              value: order.id,
              label: order.name,
            }))}
            options={orders.map((order) => ({
              value: order.id,
              label: order.name,
            }))}
            onChange={(selectedOptions) =>
              setSelectedOrders(
                selectedOptions.map((option) => ({
                  id: option.value,
                  name: option.label,
                }))
              )
            }
          />
        </Box>
        <Text alignSelf="flex-start">
          {t('table-sharing-modal.expiration.label')}
        </Text>
        <Select
          value={expiresIn}
          onChange={(event) =>
            void setExpiresIn(Number.parseInt(event.target.value))
          }
        >
          {EXPIRY_CHOICES.map((choice) => (
            <option key={choice.label} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </Select>
        {savedViews ? (
          <>
            <Text alignSelf="start">{t('table-sharing-modal.saved-view')}</Text>
            <Select
              aria-label="Saved view to activate"
              value={viewId}
              onChange={(event) => void setViewId(event.target.value)}
            >
              {!savedViews.find((view) => view.isDefault) ? (
                <option key="undefined" value={undefined}>
                  {t('saved-views.home')}
                </option>
              ) : null}
              {savedViews.map((view) => (
                <option key={view.id} value={view.id}>
                  {view.name}
                </option>
              ))}
            </Select>
          </>
        ) : null}
        {selectedOrders.length > 0 && (
          <>
            <Text alignSelf="flex-start">
              {t('table-sharing-modal.link-label')}
            </Text>
            <Flex width="100%">
              <Tooltip label={shareableUrl} openDelay={400}>
                <Input
                  aria-label="Shareable URL"
                  value={shareableUrl}
                  isDisabled={true}
                  borderRightRadius={0}
                />
              </Tooltip>
              <Button
                colorScheme="gray"
                onClick={onCopy}
                borderLeftRadius={0}
                px={6}
                fontWeight="normal"
              >
                {hasCopied
                  ? t('table-sharing-modal.link-copied')
                  : t('table-sharing-modal.copy-link')}
              </Button>
            </Flex>
          </>
        )}
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          {/* TODO: Update this to read from returned token `exp` instead */}
          <AlertDescription>
            {t('table-sharing-modal.warning-message') + ' '}
            <Text as="i" fontWeight="bold" whiteSpace="pre">
              {expiry}
            </Text>
          </AlertDescription>
        </Alert>
      </VStack>
    );
  }

  return (
    <Modal isOpen={isOpen && modalBodyElement !== null} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('table-sharing-modal.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{modalBodyElement}</ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" onClick={onClose}>
            {t('table-sharing-modal.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
